import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { X, Search, Printer, List, ChevronRight } from 'lucide-react';

import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle,
    SheetDescription,
} from '../ui/sheet';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { resolveDocContent, getDoc } from '../../lib/docsRegistry';


function slugify(text) {
    return String(text)
        .toLowerCase()
        .replace(/[^\w\s-]/g, '')
        .trim()
        .replace(/\s+/g, '-')
        .slice(0, 64);
}


/**
 * P_PLATFORM_DOCS_1.0 — reusable in-app documentation viewer.
 *
 * Every major feature can bring up its Administrator Guide (or any
 * other doc registered in `lib/docsRegistry.js`) without leaving the
 * application. Content is rendered client-side from Markdown so the
 * same source can be shipped in `docs/*.md` and shown in the UI.
 *
 * Features:
 *   * Automatic language selection (uses i18next current lang, falls
 *     back to English).
 *   * Search within the doc (client-side).
 *   * Table of contents auto-built from H2 headings; click to jump.
 *   * Collapsible TOC on mobile.
 *   * Print-friendly (delegates to window.print with a dedicated
 *     stylesheet defined in `index.css`).
 *   * Mobile responsive — Sheet slides in from the right at ≥md,
 *     covers the full screen on mobile.
 */
export const DocumentationViewer = ({ open, onOpenChange, docId }) => {
    const { t, i18n } = useTranslation();
    const contentRef = useRef(null);

    const [query, setQuery] = useState('');
    const [tocOpen, setTocOpen] = useState(false);

    const lang = (i18n.language || 'en').split('-')[0];
    const doc = docId ? getDoc(docId) : null;
    const markdown = useMemo(
        () => (docId ? resolveDocContent(docId, lang) : null),
        [docId, lang],
    );

    // Build a table of contents from the markdown (## H2 headings).
    const toc = useMemo(() => {
        if (!markdown) return [];
        const rows = [];
        markdown.split('\n').forEach((line) => {
            const m = line.match(/^##\s+(.+?)\s*$/);
            if (m) rows.push({ text: m[1], slug: slugify(m[1]) });
        });
        return rows;
    }, [markdown]);

    // Optionally filter the markdown by query — we implement it as a
    // simple visibility mask: hide sections that don't match. Sections
    // are split by H2. When the query is empty, everything is shown.
    const filteredMarkdown = useMemo(() => {
        if (!markdown || !query.trim()) return markdown;
        const needle = query.toLowerCase();
        const parts = markdown.split(/(?=^## )/m);
        return parts
            .filter((part, i) => i === 0 || part.toLowerCase().includes(needle))
            .join('');
    }, [markdown, query]);

    // Reset query and jump-to-top whenever the doc changes or the sheet opens.
    useEffect(() => {
        if (open) {
            setQuery('');
            setTocOpen(false);
            setTimeout(() => {
                contentRef.current?.scrollTo({ top: 0 });
            }, 30);
        }
    }, [open, docId]);

    const handlePrint = () => {
        // Mark the viewer for the print stylesheet.
        const root = contentRef.current?.closest('[data-docs-viewer-root]');
        if (root) root.classList.add('docs-print-active');
        window.print();
        setTimeout(() => root?.classList.remove('docs-print-active'), 500);
    };

    const jumpToHeading = (slug) => {
        const el = contentRef.current?.querySelector(`#${CSS.escape(slug)}`);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        setTocOpen(false);
    };

    if (!doc) return null;

    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent
                side="right"
                className="w-full sm:max-w-2xl md:max-w-3xl p-0 flex flex-col docs-viewer"
                data-testid={`docs-viewer-${docId}`}
                data-docs-viewer-root
            >
                <SheetHeader className="px-6 pt-6 pb-4 border-b space-y-3 shrink-0">
                    <div className="flex items-start justify-between gap-4">
                        <div className="min-w-0">
                            <SheetTitle
                                className="text-xl font-semibold text-slate-900 truncate"
                                data-testid="docs-viewer-title"
                            >
                                {t(doc.titleKey, docId)}
                            </SheetTitle>
                            <SheetDescription className="text-xs text-slate-500 mt-1">
                                {t('docs.viewer.subtitle', {
                                    defaultValue: 'In-app documentation',
                                })}
                            </SheetDescription>
                        </div>
                        <div className="flex items-center gap-1 shrink-0 docs-controls">
                            <Button
                                size="icon"
                                variant="ghost"
                                onClick={() => setTocOpen((v) => !v)}
                                title={t('docs.viewer.toc', { defaultValue: 'Contents' })}
                                data-testid="docs-viewer-toc-btn"
                                className="md:hidden"
                            >
                                <List className="h-4 w-4" />
                            </Button>
                            <Button
                                size="icon"
                                variant="ghost"
                                onClick={handlePrint}
                                title={t('docs.viewer.print', { defaultValue: 'Print' })}
                                data-testid="docs-viewer-print-btn"
                            >
                                <Printer className="h-4 w-4" />
                            </Button>
                            <Button
                                size="icon"
                                variant="ghost"
                                onClick={() => onOpenChange(false)}
                                title={t('common.close', { defaultValue: 'Close' })}
                                data-testid="docs-viewer-close-btn"
                            >
                                <X className="h-4 w-4" />
                            </Button>
                        </div>
                    </div>

                    <div className="relative">
                        <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                        <Input
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder={t('docs.viewer.searchPlaceholder', {
                                defaultValue: 'Search within this document…',
                            })}
                            className="pl-9"
                            data-testid="docs-viewer-search"
                        />
                    </div>
                </SheetHeader>

                <div className="flex-1 overflow-hidden flex min-h-0">
                    {/* TOC — always visible on md+, drawer on mobile */}
                    <aside
                        className={`
                            border-r bg-slate-50/60 overflow-y-auto text-sm
                            hidden md:block md:w-64 shrink-0 docs-toc
                            ${tocOpen ? '!block absolute inset-0 z-10 md:relative bg-white md:bg-slate-50/60' : ''}
                        `}
                        data-testid="docs-viewer-toc"
                    >
                        <nav className="py-4 px-3 space-y-0.5">
                            {toc.map((row) => (
                                <button
                                    type="button"
                                    key={row.slug}
                                    onClick={() => jumpToHeading(row.slug)}
                                    className="w-full text-left px-3 py-1.5 rounded hover:bg-slate-200/70 flex items-center gap-2 text-slate-700"
                                    data-testid={`docs-toc-${row.slug}`}
                                >
                                    <ChevronRight className="h-3 w-3 text-slate-400 shrink-0" />
                                    <span className="truncate">{row.text}</span>
                                </button>
                            ))}
                            {toc.length === 0 ? (
                                <div className="px-3 py-2 text-slate-400 text-xs">
                                    {t('docs.viewer.emptyToc', {
                                        defaultValue: 'No sections found.',
                                    })}
                                </div>
                            ) : null}
                        </nav>
                    </aside>

                    <div
                        ref={contentRef}
                        className="flex-1 overflow-y-auto docs-markdown px-6 py-6"
                        data-testid="docs-viewer-content"
                    >
                        {filteredMarkdown ? (
                            <ReactMarkdown
                                remarkPlugins={[remarkGfm]}
                                components={MARKDOWN_COMPONENTS}
                            >
                                {filteredMarkdown}
                            </ReactMarkdown>
                        ) : (
                            <div className="text-sm text-slate-500">
                                {t('docs.viewer.notFound', {
                                    defaultValue:
                                        'Documentation not available in your language yet. Try switching to English.',
                                })}
                            </div>
                        )}
                    </div>
                </div>
            </SheetContent>
        </Sheet>
    );
};

/** Markdown → JSX component overrides. Defined at module scope so
 * React does not recreate the components on every render. */
const MARKDOWN_COMPONENTS = {
    h1: ({ children }) => (
        <h1 className="text-2xl font-semibold text-slate-900 mb-4">{children}</h1>
    ),
    h2: ({ children }) => {
        const text = String(children);
        return (
            <h2
                id={slugify(text)}
                className="text-lg font-semibold text-slate-800 mt-8 mb-3 scroll-mt-6"
            >
                {children}
            </h2>
        );
    },
    h3: ({ children }) => (
        <h3 className="text-base font-semibold text-slate-800 mt-5 mb-2">{children}</h3>
    ),
    p: ({ children }) => (
        <p className="text-sm text-slate-700 leading-relaxed my-3">{children}</p>
    ),
    ul: ({ children }) => (
        <ul className="text-sm text-slate-700 leading-relaxed my-3 pl-5 list-disc space-y-1">
            {children}
        </ul>
    ),
    ol: ({ children }) => (
        <ol className="text-sm text-slate-700 leading-relaxed my-3 pl-5 list-decimal space-y-1">
            {children}
        </ol>
    ),
    code: ({ inline, children }) =>
        inline ? (
            <code className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-800 text-xs font-mono">
                {children}
            </code>
        ) : (
            <code className="block p-3 rounded bg-slate-900 text-slate-100 text-xs font-mono my-3 overflow-x-auto">
                {children}
            </code>
        ),
    table: ({ children }) => (
        <div className="overflow-x-auto my-4">
            <table className="w-full text-sm border-collapse">{children}</table>
        </div>
    ),
    th: ({ children }) => (
        <th className="text-left px-3 py-2 bg-slate-100 border-b border-slate-200 font-medium">
            {children}
        </th>
    ),
    td: ({ children }) => (
        <td className="px-3 py-2 border-b border-slate-100 align-top">{children}</td>
    ),
    blockquote: ({ children }) => (
        <blockquote className="border-l-4 border-amber-300 bg-amber-50 px-4 py-2 my-4 text-sm text-amber-900">
            {children}
        </blockquote>
    ),
    hr: () => <hr className="my-6 border-slate-200" />,
    a: ({ href, children }) => (
        <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="text-emerald-700 underline"
        >
            {children}
        </a>
    ),
};
